import { RouteObject } from 'react-router';
import Authenticated from 'src/pages/auth/Authenticated';
import AccentSidebarLayout from 'src/core/ui/menu/AccentSidebarLayout';
import { Suspense, lazy as reactLazy } from 'react';
import { Navigate } from 'react-router-dom';
import SuspenseLoader from 'src/core/ui/helpers/SuspenseLoader';
import { ErrorBoundary } from 'src/core/ui/helpers/ErrorBoundry';

// Lazy pages expose preload so the Overview can fetch a page's code on hover, before the click.
const lazy = (factory) => Object.assign(reactLazy(factory), { preload: () => factory().catch(() => undefined) });
const Loader = (Component) => Object.assign((props) =>
(
  <Suspense fallback={<SuspenseLoader />}>
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  </Suspense>
), { preload: Component.preload });

const SalesOrdersTablePage = Loader(lazy(() => import('src/pages/orders/orders/SalesOrdersTablePage')));
const SalesOrderFormPage = Loader(lazy(() => import('src/pages/orders/orders/SalesOrderFormPage')));

const ReturnOrdersTablePage = Loader(lazy(() => import('src/pages/orders/returns/ReturnOrdersTablePage')));
const TransferOrdersTablePage = Loader(lazy(() => import('src/pages/warehouses/transfers/TransferOrdersTablePage')));
const ReturnOrderFormPage = Loader(lazy(() => import('src/pages/orders/returns/ReturnOrderFormPage')));
const CategoriesTablePage = Loader(lazy(() => import('src/pages/products/categories/CategoriesTablePage')));
const ImagesResources = Loader(lazy(() => import('src/pages/products/images-resources/ImagesAndResources')));
const ManufacturersTablePage = Loader(lazy(() => import('src/pages/products/manufacturers/ManufacturersTablePage')));
const ProductTypesTablePage = Loader(lazy(() => import('src/pages/products/productTypes/ProductTypesTablePage')));
const SupplierProductsTablePage = Loader(lazy(() => import('src/pages/products/supplierProducts/SupplierProductsTablePage')));
const LowOrderPointsTablePage = Loader(lazy(() => import('src/pages/purchasing/lowOrderPoints/LowOrderPointsTablePage')));
const LowOrderPointFormPage = Loader(lazy(() => import('src/pages/purchasing/lowOrderPoints/LowOrderPointFormPage')));
const PurchaseOrderDraftsTablePage = Loader(lazy(() => import('src/pages/purchasing/purchaseOrderDraft/PurchaseOrderDraftsTablePage')));
const PurchaseOrdersTablePage = Loader(lazy(() => import('src/pages/purchasing/purchaseOrder/PurchaseOrdersTablePage')));
const PurchaseOrderFormPage = Loader(lazy(() => import('src/pages/purchasing/purchaseOrder/PurchaseOrderFormPage')));
const PurchaseOrderDraftFormPage = Loader(lazy(() => import('src/pages/purchasing/purchaseOrderDraft/PurchaseOrderDraftFormPage')));
const CustomerReturnOrders = Loader(lazy(() => import('src/pages/reports/customerReturnOrders/CustomerReturnOrders')));
const ProductRecieveReport = Loader(lazy(() => import('src/pages/reports/productRecieveReport/ProductRecieveReport')));
const CampaignOrderReport = Loader(lazy(() => import('src/pages/reports/campaignReports/campaignOrderReport/CampaignOrderReport')));
const CampaignSalesItemsSalesTablePage = Loader(lazy(() => import('src/pages/reports/campaignReports/salesItemsSales/CampaignSalesItemsSalesTablePage')));
const SalesItemsSales = Loader(lazy(() => import('src/pages/reports/salesReports/salesItemsSales/SalesItemsSales')));
const SalesOrderReport = Loader(lazy(() => import('src/pages/reports/salesReports/salesOrderReport/SalesOrderReport')));
const ProductsHomePage = Loader(lazy(() => import('src/pages/products/ProductsPagesOverview')));
const OrdersHomePage = Loader(lazy(() => import('src/pages/products/ProductsPagesOverview')));
const ReportsHomePage = Loader(lazy(() => import('src/pages/reports/Reports')));

const WarehousesTablePage = Loader(lazy(() => import('src/pages/warehouses/warehouses/WarehousesTablePage')));

// Product
const ProductsTablePage = Loader(lazy(() => import('src/pages/products/products/ProductsTablePage')));
const ProductFormPage = Loader(lazy(() => import('src/pages/products/products/ProductFormPage')));
const SalesItemsTablePage = Loader(lazy(() => import('src/pages/products/salesItems/SalesItemsTablePage')));
const SalesItemFormPage = Loader(lazy(() => import('src/pages/products/salesItems/SalesItemFormPage')));
const SupplierProductFormPage = Loader(lazy(() => import('src/pages/products/supplierProducts/SupplierProductFormPage')));
const SuppliersTablePage = Loader(lazy(() => import('src/pages/products/suppliers/SuppliersTablePage')));
const SupplierFormPage = Loader(lazy(() => import('src/pages/products/suppliers/SupplierFormPage')));


//Account 
const Register = Loader(lazy(() => import('src/pages/auth/Register')));
import Logout from 'src/pages/auth/Logout';
import WarehouseFormPage from './pages/warehouses/warehouses/WarehouseFormPage';
import TransferOrderFormPage from './pages/warehouses/transfers/TransferOrderFormPage';
import BackordersTablePage from './pages/purchasing/backorders/BackordersTablePage';
import WarehouseValueSummaryReportTablePage from './pages/reports/warehouse/WarehouseValueSummaryReportTablePage';
import FulfillmentOrdersTablePage from './pages/warehouses/fulfillment/FulfillmentOrdersTablePage';
import CreditNotesTablePage from './pages/orders/payments/creditNotes/CreditNotesTablePage';
import CurrenciesTablePage from './pages/settings/admin/currencies/CurrenciesTablePage';
import SalesShippingMethodsTablePage from './pages/settings/admin/salesShippingMethods/SalesShippingMethodsTablePage';
import BillableOrdersTablePage from './pages/orders/payments/billableOrders/BillableOrdersTablePage';
import WarehouseShippingMethodsTablePage from './pages/settings/admin/warehouseShippingMethods/WarehouseShippingMethodsTablePage';
import PurchasingShippingMethodsTablePage from './pages/settings/admin/purchasingShippingMethods/PurchasingShippingMethodsTablePage';
import PurchasingShippingTermsTablePage from './pages/settings/admin/purchasingShippingTerms/PurchasingShippingTermsTablePage';
import OrderOriginsTablePage from './pages/settings/admin/orderOrigins/OrderOriginsTablePage';
import TaxRatesTablePage from './pages/settings/admin/taxRates/TaxRatesTablePage';
import ProductLinesTablePage from './pages/products/productLines/ProductLinesTablePage';
import ProductLineFormPage from './pages/products/productLines/ProductLineFormPage';
import { Kalle3 } from './pages/testing/kalle/Kalle3';
import { StatusPage } from './pages/testing/test/Status';
import OverviewPage from './pages/OverviewPage';
import InvoicesTablePage from './pages/orders/payments/invoices/InvoicesTablePage';
import InvoiceFormPage from './pages/orders/payments/invoices/InvoiceFormPage';
import DashboardMain from './pages/dashboard/DashboardMain';
import CampaignsTablePage from './pages/orders/campaigns/CampaignsTablePage';
import CampaignFormPage from './pages/orders/campaigns/CampaignFormPage';
import CustomerFormPage from './pages/orders/customers/CustomerFormPage';
import CustomersTablePage from './pages/orders/customers/CustomersTablePage';
import PurchaseOrderWithAIFormPage from './pages/purchasing/purchaseOrder/PurchaseOrderWithAIFormPage';
import SetupMFA from './pages/auth/SetupMFA';
import Printing from './pages/testing/test/Printing';
import DispatchOrderFormPage from './pages/warehouses/dispatch/DispatchOrderFormPage';
import DispatchOrdersTablePage from './pages/warehouses/dispatch/DispatchOrdersTablePage';
import ReceivingOrdersTablePage from './pages/warehouses/receiving/ReceivingOrdersTablePage';
import ReceivingOrderFormPage from './pages/warehouses/receiving/ReceivingOrderFormPage';
import RelocateReceivedProductsTablePage from './pages/warehouses/relocate/RelocateReceivedProductsTablePage';
import StockCorrectionTablePage from './pages/warehouses/stocktaking/StockCorrectionTablePage';
import RelocateProductsTablePage from './pages/warehouses/relocate/RelocateProductsTablePage';

// Settings
const WebclientSettingsFormPage = Loader(lazy(() => import('src/pages/settings/WebclientSettingsFormPage')));

// Testing
const TestingOverView = Loader(lazy(() => import('src/pages/testing/test/Test')));
const IntegrationTesting = Loader(lazy(() => import('src/pages/testing/test/IntegrationTesting')));
const KalleTesting = Loader(lazy(() => import('src/pages/testing/kalle/Kalle')));
const KalleTesting2 = Loader(lazy(() => import('src/pages/testing/kalle/Kalle2')));
const ContentCardTemplate = Loader(lazy(() => import('src/pages/templates/card/ContentCardTemplate')));
const FormikTemplate = Loader(lazy(() => import('src/pages/templates/formik/FormikTemplate')));
const DataGridTemplate = Loader(lazy(() => import('src/pages/templates/dataGrid/DataGridTemplate')));
const ErikTesting = Loader(lazy(() => import('src/pages/testing/erik/Erik')));
const HannesTesting = Loader(lazy(() => import('src/pages/testing/hannes')));
const OlleTesting = Loader(lazy(() => import('src/pages/testing/olle/Olle')));
const ApiTesting = Loader(lazy(() => import('src/pages/testing/api/Api')));
const Api2Testing = Loader(lazy(() => import('src/pages/testing/api/Api2')));
const Api3Testing = Loader(lazy(() => import('src/pages/testing/api/Api3')));





export const pagesRoutes = [

  {
    path: 'products',
    children: [
      {
        path: '',
        element: <OverviewPage />
      },
      {
        path: 'products',
        element: <ProductsTablePage />
      },

      {
        path: 'products',
        children: [
          {
            path: 'create',
            element: <ProductFormPage />
          },
          {
            path: 'edit',
            element: <ProductFormPage />
          },


        ]
      },
      {
        path: 'product-types',
        element: <ProductTypesTablePage />
      },
      {
        path: 'product-lines',
        element: <ProductLinesTablePage />
      },
      {
        path: 'product-lines',
        children: [
          {
            path: "create/",
            element: <ProductLineFormPage />
          },
          {
            path: "edit/",
            element: <ProductLineFormPage />
          },




        ]
      },

      {
        path: 'sales-items',
        element: <SalesItemsTablePage />
      },
      {
        path: 'sales-items',
        children: [
          {
            path: "create/",
            element: <SalesItemFormPage />
          },
          {
            path: "edit/",
            element: <SalesItemFormPage />
          },




        ]
      },
      {
        path: "sales-items/:categories",
        element: <SalesItemsTablePage />
      },
      {
        path: 'categories',
        element: <CategoriesTablePage />
      },
      {
        path: 'supplier-products',
        element: <SupplierProductsTablePage />,
      },
      {
        path: 'suppliers',
        element: <SuppliersTablePage />,
      },
      {
        path: 'suppliers',
        children: [
          {
            path: "create/",
            element: <SupplierFormPage />
          },
          {
            path: "edit/",
            element: <SupplierFormPage />
          },




        ]
      },
      {
        path: 'supplier-products',
        children: [
          {
            path: "create/",
            element: <SupplierProductFormPage />
          },
          {
            path: "edit/",
            element: <SupplierProductFormPage />
          },




        ]
      },
      {
        path: 'manufacturers',
        element: <ManufacturersTablePage />
      },
      {
        path: 'resources',
        element: <ImagesResources />
      },

    ]


  },
  {
    path: 'reports',
    children: [
      {
        path: '',
        element: <OverviewPage />
      },
      {
        path: 'campaign-reports',
        children: [
          {
            path: 'sales-order-report',
            element: <CampaignOrderReport />
          },
          {
            path: 'sales-items-report',
            element: <CampaignSalesItemsSalesTablePage />
          },
        ]
      },
      {
        path: 'warehouse',
        children: [
          {
            path: 'values-summary',
            element: <WarehouseValueSummaryReportTablePage />
          },
          {
            path: 'sales-items-report',
            element: <CampaignSalesItemsSalesTablePage />
          },
        ]
      },
      {
        path: 'sales-reports',
        children: [
          {
            path: 'sales-order-reports',
            element: <SalesOrderReport />
          },
          {
            path: 'sales-items-sales',
            element: <SalesItemsSales />
          },
        ]
      },

      {
        path: 'product-recieve-report',
        element: <ProductRecieveReport />
      },
      {
        path: 'customer-return-order-report',
        element: <CustomerReturnOrders />
      },


    ]
  },
  {
    path: 'settings',
    children: [
      {
        path: '',
        element: <Navigate to="webclient" replace />
      },
      {
        path: 'webclient',
        element: <WebclientSettingsFormPage />
      },

      {
        path: 'admin',
        children: [
          {
            path: 'currencies',
            element: <CurrenciesTablePage />
          },
          {
            path: 'order-origins',
            element: <OrderOriginsTablePage />
          },
          {
            path: 'tax-rates',
            element: <TaxRatesTablePage />
          },
          {
            path: 'shipping-methods',
            element: <SalesShippingMethodsTablePage />
          },
          {
            path: 'warehouse-shipping-methods',
            element: <WarehouseShippingMethodsTablePage />
          },
          {
            path: 'purchasing-shipping-methods',
            element: <PurchasingShippingMethodsTablePage />
          },
          {
            path: 'purchasing-shipping-terms',
            element: <PurchasingShippingTermsTablePage />
          },

        ]
      },



    ]
  },
  {
    path: 'accounts',
    children: [
      {
        path: 'register',
        element: (
          <Register />
        )
      },
      {
        path: 'setup-mfa',
        element: (
          <SetupMFA />
        )
      },
      {
        path: 'logout',
        element: (
          <Logout />
        )
      },



    ]
  },

  {
    path: 'testing',
    children: [
      {
        path: '',
        element: <Navigate to="sales-items" replace />
      },

      {
        path: 'test',
        element: <TestingOverView />
      },
      {
        path: 'integration-test',
        element: <IntegrationTesting />
      },
      {
        path: 'kalle',
        element: <KalleTesting />
      },
      {
        path: 'kalle2',
        element: <KalleTesting2 />
      },
      {
        path: 'kalle3',
        element: <Kalle3 />
      },
      {
        path: 'Printing',
        element: <Printing />
      },
      {
        path: 'status',
        element: <StatusPage />
      },
      {
        path: 'erik',
        element: <ErikTesting />
      },
      {
        path: 'hannes',
        element: <HannesTesting />
      },
      {
        path: 'olle',
        element: <OlleTesting />
      },
      {
        path: 'api',
        element: <ApiTesting />
      },
      {
        path: 'api2',
        element: <Api2Testing />
      },
      {
        path: 'api3',
        element: <Api3Testing />
      },
    ]
  },
  {
    path: 'templates',
    children: [
      {
        path: 'cards',
        element: <ContentCardTemplate />
      },
      {
        path: 'formik',
        element: <FormikTemplate />
      },
      {
        path: 'datagrid',
        element: <DataGridTemplate />
      },

    ]
  },
  {
    path: 'orders',
    children: [
      {
        path: '',
        element: <OverviewPage />
      },
      {
        path: 'orders',
        element: <SalesOrdersTablePage />
      },
      {
        path: 'orders',
        children: [
          {
            path: "create",
            element: <SalesOrderFormPage />
          },
          {
            path: "edit",
            element: <SalesOrderFormPage />
          },




        ]
      },
      {
        path: 'customers',
        element: <CustomersTablePage />
      },
      {
        path: 'customers',
        children: [
          {
            path: "create",
            element: <CustomerFormPage />
          },
          {
            path: "edit",
            element: <CustomerFormPage />
          },




        ]
      },
      {
        path: 'returns',
        element: <ReturnOrdersTablePage />

      },
      {
        path: 'returns',
        children: [
          {
            path: "create",
            element: <ReturnOrderFormPage />
          },
          {
            path: "edit",
            element: <ReturnOrderFormPage />
          },




        ]
      },
      {
        path: 'campaigns',
        element: <CampaignsTablePage/>

      },
      {
        path: 'campaigns',
        children: [
          {
            path: "create",
            element: <CampaignFormPage />
          },
          {
            path: "edit",
            element: <CampaignFormPage />
          },




        ]
      },
      {
        path: 'payments',
        children: [
          {
            path: "credit-notes",
            element: <CreditNotesTablePage />
          },
          {
            path: "billable-orders",
            element: <BillableOrdersTablePage />
          },
          {
            path:"invoices",
            element:<InvoicesTablePage />
          },
          {
            path: 'invoices',
            children: [
              {
                path: "create",
                element: <InvoiceFormPage />
              },
              {
                path: "edit",
                element: <InvoiceFormPage />
              },
    
    
    
    
            ]
          },


        ]
      },

    ]
  },
  {
    path: 'purchasing',
    children: [
      {
        path: '',
        element:<OverviewPage />


      },

      {
        path: 'purchase-order',
        element: <PurchaseOrdersTablePage />
      },
      {
        path: 'purchase-order',
        children: [
          {
            path: "create",
            element: <PurchaseOrderFormPage />
          },
          {
            path: "edit",
            element: <PurchaseOrderFormPage />
          },
          {
            path: "create-test",
            element: <PurchaseOrderWithAIFormPage />
          },
          {
            path: "edit-test",
            element: <PurchaseOrderWithAIFormPage />
          },

          


        ]
      },
      {
        path: 'backorders',
        element: <BackordersTablePage />
      },
      {
        path: 'purchase-order-draft',
        element: <PurchaseOrderDraftsTablePage />
      },
      {
        path: 'purchase-order-draft',
        children: [
          {
            path: "create",
            element: <PurchaseOrderDraftFormPage />
          },
          {
            path: "edit",
            element: <PurchaseOrderDraftFormPage />
          },




        ]
      },
      {
        path: 'low-order-points',
        element: <LowOrderPointsTablePage />
      },
      {
        path: 'low-order-points',
        children: [
          {
            path: "create",
            element: <LowOrderPointFormPage />
          },
          {
            path: "edit",
            element: <LowOrderPointFormPage />
          },]
      },

      {
        path: 'suppliers',
        element: <SuppliersTablePage />
      },
    ]
  },

  {
    path: 'warehouse',
    children: [
      {
        path: '',
        element: <OverviewPage />
      },
      {
        path: 'product-overview',
        element: <WarehousesTablePage />
      },
      {
        path: 'warehouses',
        element: <WarehousesTablePage />
      },
      {
        path: 'fullfillment-orders',
        element: <FulfillmentOrdersTablePage />
      },
      {
        path: 'warehouses',
        children: [
          {
            path: "create",
            element: <WarehouseFormPage />
          },
          {
            path: "edit",
            element: <WarehouseFormPage />
          },




        ]
      },
      {
        path: 'dispatch-orders',
        element: <DispatchOrdersTablePage />
      },
      {
        path: 'dispatch-orders-printer',
        element: <DispatchOrdersTablePage />
      },
      {
        path: 'dispatch-orders',
        children: [
          {
            path: "create",
            element: <DispatchOrderFormPage />
          },
          {
            path: "edit",
            element: <DispatchOrderFormPage />
          },
     




        ]
      },
      {
        path: 'relocate-received-products',
        element: <RelocateReceivedProductsTablePage />
      },
      {
        path: 'relocate-products',
        element: <RelocateProductsTablePage />
      },
      {
        path: 'receiving-orders',
        element: <ReceivingOrdersTablePage />
      },
      {
        path: 'stock-corrections',
        element: <StockCorrectionTablePage />
      },
      {
        path: 'receiving-orders',
        children: [
          {
            path: "create",
            element: <ReceivingOrderFormPage />
          },
          {
            path: "edit",
            element: <ReceivingOrderFormPage />
          },
     




        ]
      },
      {
        path: 'transfers',
        element: <TransferOrdersTablePage />
      },
      {
        path: 'transfers',
        children: [
          {
            path: "create",
            element: <TransferOrderFormPage />
          },
          {
            path: "edit",
            element: <TransferOrderFormPage />
          },




        ]
      },
    ]
  }

];







const DashboardDefault = Loader(lazy(() => import('src/pages/dashboard/DashboardDemo')));
const router: RouteObject[] = [

  {
    path: '',
    element: <AccentSidebarLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboards" replace />
      },
      {
        path: 'overview',
        element: <Navigate to="/dashboards" replace />
      }
    ]
  },
//   {
//     path: '/login',
//     element: <Login />
//  },

  {
    path: '',
    element: (
      <Authenticated>
        <AccentSidebarLayout />
      </Authenticated>
    ),
    children: [
      {
        path: '',
        children: pagesRoutes
      },
      {
        path: 'dashboards',
        children: [
          {
            path: '',
            element: (
              <DashboardMain />
            )
          }

        ]
      },
      {
        path: 'dashboards/demo',
        children: [
          {
            path: '',
            element: (
              <DashboardDefault />
            )
          }

        ]
      },

    ]
  }
];

export default router;

